import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Plus, Edit, Copy, Trash2 } from 'lucide-react';
import { AddProductModal } from './AddProductModal';
import { CreateBundleModal } from './CreateBundleModal';
import { Modal } from '../ui/Modal';
import { useWizardOptional } from '../../hooks/useWizard';
interface Product {
  id: string;
  name: string;
  category: string;
  description: string;
  basePrice: string;
  sizeGroup: 'youth' | 'adult' | 'both';
  sizes: string[];
  colors: Array<{
    name: string;
    hex: string;
  }>;
  limitInventory: boolean;
  inventoryCaps: Record<string, number>;
  decorationMethod: 'print' | 'embroidery';
  decorationLocations: string[];
  personalizationFields: any[];
}
interface Bundle {
  id: string;
  name: string;
  price: string;
  products: Array<{
    productId: string;
    productName: string;
    requireSizeSelection: boolean;
  }>;
}
export function WizardStep4Products() {
  // Use optional wizard context - if it exists, use it; otherwise use local state
  const wizardContext = useWizardOptional();
  // Local state fallback for when not in wizard context
  const [localProducts, setLocalProducts] = useState<Product[]>([]);
  const [localBundles, setLocalBundles] = useState<Bundle[]>([]);
  // Determine which state to use
  const products = wizardContext ? wizardContext.data.step4.products as Product[] : localProducts;
  const bundles = wizardContext ? wizardContext.data.step4.bundles as Bundle[] : localBundles;
  const updateProducts = (newProducts: Product[]) => {
    if (wizardContext) {
      wizardContext.updateStep4({
        products: newProducts
      });
    } else {
      setLocalProducts(newProducts);
    }
  };
  const updateBundles = (newBundles: Bundle[]) => {
    if (wizardContext) {
      wizardContext.updateStep4({
        bundles: newBundles
      });
    } else {
      setLocalBundles(newBundles);
    }
  };
  const [isAddProductOpen, setIsAddProductOpen] = useState(false);
  const [isCreateBundleOpen, setIsCreateBundleOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [editingBundle, setEditingBundle] = useState<Bundle | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    type: 'product' | 'bundle';
    id: string;
  } | null>(null);
  const handleSaveProduct = (productData: any) => {
    if (editingProduct) {
      const updatedProducts = products.map(p => p.id === editingProduct.id ? {
        ...productData,
        id: p.id
      } : p);
      updateProducts(updatedProducts);
      setEditingProduct(null);
    } else {
      const newProduct = {
        ...productData,
        id: `prod_${Date.now()}`
      };
      updateProducts([...products, newProduct]);
    }
    setIsAddProductOpen(false);
  };
  const handleDuplicateProduct = (product: Product) => {
    const duplicated = {
      ...product,
      id: `prod_${Date.now()}`,
      name: `${product.name} (Copy)`
    };
    updateProducts([...products, duplicated]);
  };
  const handleDeleteProduct = (id: string) => {
    updateProducts(products.filter(p => p.id !== id));
    setDeleteConfirm(null);
  };
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setIsAddProductOpen(true);
  };
  const handleSaveBundle = (bundleData: any) => {
    if (editingBundle) {
      const updatedBundles = bundles.map(b => b.id === editingBundle.id ? {
        ...bundleData,
        id: b.id
      } : b);
      updateBundles(updatedBundles);
      setEditingBundle(null);
    } else {
      const newBundle = {
        ...bundleData,
        id: `bundle_${Date.now()}`
      };
      updateBundles([...bundles, newBundle]);
    }
    setIsCreateBundleOpen(false);
  };
  const handleDeleteBundle = (id: string) => {
    updateBundles(bundles.filter(b => b.id !== id));
    setDeleteConfirm(null);
  };
  const handleEditBundle = (bundle: Bundle) => {
    setEditingBundle(bundle);
    setIsCreateBundleOpen(true);
  };
  const getVariantSummary = (product: Product) => {
    const sizeCount = product.sizes.length;
    const colorCount = product.colors.length;
    const totalVariants = sizeCount * colorCount;
    return `${totalVariants} variants (${sizeCount} sizes • ${colorCount} colors)`;
  };
  const getDecorationSummary = (product: Product) => {
    const method = product.decorationMethod.charAt(0).toUpperCase() + product.decorationMethod.slice(1);
    const locations = product.decorationLocations.join(' + ');
    return locations ? `${method}: ${locations}` : method;
  };
  return <div className="space-y-6" data-id="element-501">
      <div className="flex justify-between items-center" data-id="element-502">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-503">
          Products & Decoration
        </h3>
        <Button onClick={() => {
        setEditingProduct(null);
        setIsAddProductOpen(true);
      }} data-id="element-504">
          <Plus className="h-4 w-4 mr-2" data-id="element-505" />
          Add Product
        </Button>
      </div>

      <Card data-id="element-506">
        {products.length === 0 ? <div className="text-center py-12 bg-gray-50 rounded-lg" data-id="element-507">
            <p className="text-gray-500 mb-4" data-id="element-508">No products added yet.</p>
            <Button onClick={() => setIsAddProductOpen(true)} data-id="element-509">
              <Plus className="h-4 w-4 mr-2" data-id="element-510" />
              Add Your First Product
            </Button>
          </div> : <div className="space-y-4" data-id="element-511">
            {products.map(product => <div key={product.id} className="flex items-start space-x-4 p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-512">
                <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-md flex items-center justify-center" data-id="element-513">
                  <span className="text-xs text-gray-400" data-id="element-514">Image</span>
                </div>

                <div className="flex-1 min-w-0" data-id="element-515">
                  <div className="flex items-start justify-between" data-id="element-516">
                    <div data-id="element-517">
                      <h4 className="text-base font-medium text-gray-900" data-id="element-518">
                        {product.name}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-519">
                        {product.category}
                      </p>
                    </div>
                    <div className="text-right" data-id="element-520">
                      <p className="text-lg font-bold text-gray-900" data-id="element-521">
                        ${parseFloat(product.basePrice).toFixed(2)}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-522">base price</p>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 text-xs" data-id="element-523">
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-blue-100 text-blue-700" data-id="element-524">
                      {getVariantSummary(product)}
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-full bg-purple-100 text-purple-700" data-id="element-525">
                      {getDecorationSummary(product)}
                    </span>
                    {product.personalizationFields.length > 0 && <span className="inline-flex items-center px-2 py-1 rounded-full bg-green-100 text-green-700" data-id="element-526">
                        {product.personalizationFields.length} personalization
                        field
                        {product.personalizationFields.length > 1 ? 's' : ''}
                      </span>}
                  </div>

                  <div className="mt-3 flex items-center space-x-3" data-id="element-527">
                    <button onClick={() => handleEditProduct(product)} className="text-sm text-gray-600 hover:text-red-600 flex items-center transition-colors" data-id="element-528">
                      <Edit className="h-4 w-4 mr-1" data-id="element-529" />
                      Edit
                    </button>
                    <button onClick={() => handleDuplicateProduct(product)} className="text-sm text-gray-600 hover:text-red-600 flex items-center transition-colors" data-id="element-530">
                      <Copy className="h-4 w-4 mr-1" data-id="element-531" />
                      Duplicate
                    </button>
                    <button onClick={() => setDeleteConfirm({
                type: 'product',
                id: product.id
              })} className="text-sm text-red-600 hover:text-red-700 flex items-center transition-colors" data-id="element-532">
                      <Trash2 className="h-4 w-4 mr-1" data-id="element-533" />
                      Remove
                    </button>
                  </div>
                </div>
              </div>)}
          </div>}
      </Card>

      <Card title="Product Bundles" description={bundles.length === 0 ? 'Create packages of products that must be purchased together.' : undefined} action={<Button variant="secondary" size="sm" onClick={() => {
      setEditingBundle(null);
      setIsCreateBundleOpen(true);
    }} disabled={products.length === 0} data-id="element-535">
            <Plus className="h-4 w-4 mr-1" data-id="element-536" />
            Create Bundle
          </Button>} data-id="element-534">
        {bundles.length === 0 ? <div className="text-center py-8" data-id="element-537">
            {products.length === 0 ? <p className="text-sm text-gray-500" data-id="element-538">
                Add products first before creating bundles.
              </p> : <p className="text-sm text-gray-500" data-id="element-539">No bundles created yet.</p>}
          </div> : <div className="space-y-3" data-id="element-540">
            {bundles.map(bundle => <div key={bundle.id} className="flex items-start justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-id="element-541">
                <div className="flex-1" data-id="element-542">
                  <div className="flex items-start justify-between" data-id="element-543">
                    <div data-id="element-544">
                      <h4 className="text-base font-medium text-gray-900" data-id="element-545">
                        {bundle.name}
                      </h4>
                      <p className="text-sm text-gray-500 mt-1" data-id="element-546">
                        {bundle.products.length} product
                        {bundle.products.length > 1 ? 's' : ''} included
                      </p>
                    </div>
                    <p className="text-lg font-bold text-gray-900" data-id="element-547">
                      ${parseFloat(bundle.price).toFixed(2)}
                    </p>
                  </div>

                  <ul className="mt-2 text-xs text-gray-600 space-y-1" data-id="element-548">
                    {bundle.products.map(p => <li key={p.productId} data-id="element-549">
                        • {p.productName}{' '}
                        {p.requireSizeSelection && '(size required)'}
                      </li>)}
                  </ul>

                  <div className="mt-3 flex items-center space-x-3" data-id="element-550">
                    <button onClick={() => handleEditBundle(bundle)} className="text-sm text-gray-600 hover:text-red-600 flex items-center transition-colors" data-id="element-551">
                      <Edit className="h-4 w-4 mr-1" data-id="element-552" />
                      Edit
                    </button>
                    <button onClick={() => setDeleteConfirm({
                type: 'bundle',
                id: bundle.id
              })} className="text-sm text-red-600 hover:text-red-700 flex items-center transition-colors" data-id="element-553">
                      <Trash2 className="h-4 w-4 mr-1" data-id="element-554" />
                      Remove
                    </button>
                  </div>
                </div>
              </div>)}
          </div>}
      </Card>

      <AddProductModal isOpen={isAddProductOpen} onClose={() => {
      setIsAddProductOpen(false);
      setEditingProduct(null);
    }} onSave={handleSaveProduct} editProduct={editingProduct} data-id="element-555" />

      <CreateBundleModal isOpen={isCreateBundleOpen} onClose={() => {
      setIsCreateBundleOpen(false);
      setEditingBundle(null);
    }} onSave={handleSaveBundle} availableProducts={products.map(p => ({
      id: p.id,
      name: p.name
    }))} editBundle={editingBundle} data-id="element-556" />

      {deleteConfirm && <Modal isOpen={true} onClose={() => setDeleteConfirm(null)} title={`Delete ${deleteConfirm.type === 'product' ? 'Product' : 'Bundle'}?`} size="sm" footer={<>
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} data-id="element-558">
                Cancel
              </Button>
              <Button variant="danger" onClick={() => {
        if (deleteConfirm.type === 'product') {
          handleDeleteProduct(deleteConfirm.id);
        } else {
          handleDeleteBundle(deleteConfirm.id);
        }
      }} data-id="element-559">
                Delete
              </Button>
            </>} data-id="element-557">
          <p className="text-sm text-gray-600" data-id="element-560">
            Are you sure you want to delete this {deleteConfirm.type}? This
            action cannot be undone.
          </p>
        </Modal>}
    </div>;
}